"use client";

import { useState, useEffect } from "react";
import NextImage from "next/image";
import { useRouter } from "next/navigation";
import { Navbar, Footer } from "@/components/layout/NavbarFooter";
import MapSection from "@/components/ui/MapSection";
import ContactModal from "@/components/ui/ContactModal";
import Counter from "@/components/ui/Counter";
import { serviceService } from "@/services/serviceService";
import { Service } from "@/types";
import * as LucideIcons from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { ecosystemServices, ecosystemStats } from "@/data/ecosystemData";
import EcosystemHeroScene from "@/components/ui/EcosystemHeroScene";

const iconMap: Record<string, any> = {
  Heart: LucideIcons.Heart,
  ShieldCheck: LucideIcons.ShieldCheck,
  Users: LucideIcons.Users,
  Zap: LucideIcons.Zap,
  Cloud: LucideIcons.Cloud,
  Code: LucideIcons.Code,
  School: LucideIcons.School,
  Video: LucideIcons.Video,
  TrendingUp: LucideIcons.TrendingUp,
  Rocket: LucideIcons.Rocket,
  Shield: LucideIcons.Shield,
  Globe: LucideIcons.Globe,
  Cpu: LucideIcons.Cpu,
  BrainCircuit: LucideIcons.BrainCircuit,
  Code2: LucideIcons.Code2,
  Building2: LucideIcons.Building2,
  Presentation: LucideIcons.Presentation,
  GraduationCap: LucideIcons.GraduationCap,
  Truck: LucideIcons.Truck,
};

export default function EcosystemPageClient() {
  const router = useRouter();
  const { t } = useLanguage();
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setIsLoading(true);
        const data = await serviceService.getServices();
        setServices(data.slice(0, 20)); // Buffer more for "Load More"
      } catch (err) {
        console.error("Failed to fetch services:", err);
        setServices(ecosystemServices);
      } finally {
        setIsLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    }, observerOptions);

    const timeoutId = setTimeout(() => {
      const revealElements = document.querySelectorAll(".reveal");
      revealElements.forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [isLoading]);

  return (
    <>
      <Navbar />
      <main className="bg-white text-brand-dark antialiased">
        {/* Improved Hero Section */}
        <section className="relative min-h-screen flex items-center overflow-hidden py-24 lg:py-32">
          {/* Dynamic Three.js Background */}
          <EcosystemHeroScene />

          <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
            <div className="reveal reveal-up inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0a192f]/50 backdrop-blur-md shadow-sm border border-[#00f3ff]/20 text-[#00f3ff] font-bold text-[10px] uppercase tracking-[0.2em] mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f3ff] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00f3ff] shadow-[0_0_8px_#00f3ff]"></span>
              </span>
              {t("ecosystemPage.hero.badge")}
            </div>

            <h1 className="reveal reveal-up delay-100 text-6xl lg:text-8xl font-black tracking-tighter mb-10 max-w-5xl mx-auto leading-tight text-white">
              {t("ecosystemPage.hero.title")}{" "}
              <span className="text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                {t("ecosystemPage.hero.titleAccent")}
              </span>
            </h1>

            <p className="reveal reveal-up delay-200 text-xl lg:text-2xl text-slate-300 max-w-3xl mx-auto mb-16 leading-relaxed font-light">
              {t("ecosystemPage.hero.subtitle")}
            </p>

            <div className="reveal reveal-up delay-300 flex flex-col sm:flex-row items-center justify-center gap-6">
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="w-full sm:w-auto bg-[#00f3ff] text-[#040d21] px-10 py-5 rounded-2xl font-black text-lg hover:shadow-[0_0_30px_rgba(0,243,255,0.6)] hover:bg-[#00f3ff]/90 transition-all hover:-translate-y-1 cursor-pointer"
              >
                {t("ecosystemPage.hero.cta")}
              </button>
              <button className="w-full sm:w-auto bg-[#0a192f]/60 backdrop-blur-md text-white border border-[#00f3ff]/50 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-[#00f3ff]/20 transition-all cursor-pointer">
                {t("ecosystemPage.hero.viewProjects")}
              </button>
            </div>
          </div>
        </section>

        {/* Redesigned Service Cards Section */}
        <section className="py-32 bg-[#0a192f]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="reveal reveal-up mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
              <div className="max-w-2xl">
                <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-white">
                  {t("ecosystemPage.services.title")}
                </h2>
                <div className="h-1.5 w-24 bg-[#00f3ff] rounded-full mb-6 shadow-[0_0_10px_#00f3ff]"></div>
                <p className="text-slate-300 text-lg">
                  {t("ecosystemPage.services.subtitle")}
                </p>
              </div>
              <div className="h-px flex-1 bg-[#00f3ff]/20 hidden lg:block mb-6 mx-10"></div>
              <p className="text-slate-400 text-lg max-w-md lg:mb-4">
                {t("ecosystemPage.services.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {isLoading
                ? Array(4)
                    .fill(0)
                    .map((_, i) => (
                      <div
                        key={i}
                        className="bg-[#040d21]/60 backdrop-blur-xl p-10 rounded-[2.5rem] border border-white/5 animate-pulse h-[400px]"
                      ></div>
                    ))
                : services.slice(0, visibleCount).map((service, idx) => {
                    const delays = [
                      "delay-100",
                      "delay-200",
                      "delay-300",
                      "delay-400",
                    ];

                    return (
                      <div
                        key={service.id}
                        className={`animate-fade-in group bg-[#040d21]/60 backdrop-blur-xl p-10 rounded-[2.5rem] border border-white/10 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] transition-all duration-500 hover:-translate-y-4 cursor-pointer relative overflow-hidden`}
                        onClick={() => router.push(`/ecosystem/${service.slug}`)}
                      >
                        {/* Branded Glow */}
                        <div
                          className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] opacity-0 group-hover:opacity-20 transition-opacity duration-500"
                          style={{
                            backgroundColor: service.themeColor || "#00f3ff",
                          }}
                        ></div>

                        <div
                          className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-8 group-hover:scale-110 transition-all duration-500 border border-white/10`}
                          style={{
                            backgroundColor: service.themeColor || "#00f3ff",
                          }}
                        >
                          {(() => {
                            const IconComponent =
                              iconMap[service.icon] || LucideIcons.Zap;
                            return (
                              <IconComponent className="w-8 h-8 drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                            );
                          })()}
                        </div>
                        <h3
                          className={`text-2xl font-extrabold mb-4 text-white group-hover:text-[#00f3ff] transition-colors`}
                        >
                          {t(`ecosystemPage.services.items.${service.slug}.title`) !== `ecosystemPage.services.items.${service.slug}.title` 
                            ? t(`ecosystemPage.services.items.${service.slug}.title`) 
                            : (service.title || service.name)}
                        </h3>
                        <p 
                          className="text-slate-400 text-base leading-relaxed mb-8 font-medium line-clamp-3"
                          dangerouslySetInnerHTML={{
                            __html: t(`ecosystemPage.services.items.${service.slug}.description`) !== `ecosystemPage.services.items.${service.slug}.description`
                              ? t(`ecosystemPage.services.items.${service.slug}.description`)
                              : service.description
                          }}
                        />
                        <button
                          className="inline-flex items-center gap-2 font-bold text-sm uppercase tracking-widest group-hover:gap-4 transition-all cursor-pointer"
                          style={{ color: service.themeColor || "#00f3ff" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(`/ecosystem/${service.slug}`);
                          }}
                        >
                          {t("ecosystemPage.services.viewMore")}{" "}
                          <LucideIcons.ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
            </div>

            {/* Load More Button */}
            {!isLoading && services.length > visibleCount && (
              <div className="mt-16 flex justify-center reveal reveal-up">
                <button
                  onClick={() => setVisibleCount(prev => prev + 4)}
                  className="group relative flex items-center gap-3 px-10 py-5 bg-[#0a192f] border border-[#00f3ff]/30 rounded-2xl text-white font-bold text-lg hover:border-[#00f3ff] hover:bg-[#00f3ff]/10 transition-all duration-300 shadow-[0_0_20px_rgba(0,0,0,0.3)] hover:shadow-[0_0_30px_rgba(0,243,255,0.2)]"
                >
                  <span className="relative z-10">{t("ecosystemPage.services.viewMore")}</span>
                  <LucideIcons.Plus className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                  
                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#00f3ff] rounded-tr-lg opacity-50 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#00f3ff] rounded-bl-lg opacity-50 group-hover:opacity-100 transition-opacity"></div>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Competitive Advantages Section */}
        <section className="py-24 bg-[#040d21] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#00f3ff]/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-20 reveal reveal-up">
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-6 text-white">
                {t("ecosystemPage.advantages.title")}{" "}
                <span className="text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                  {t("ecosystemPage.advantages.titleAccent")}
                </span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed font-light">
                {t("ecosystemPage.advantages.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: "ShieldCheck",
                  delay: "delay-100",
                },
                {
                  icon: "Zap",
                  delay: "delay-200",
                },
                {
                  icon: "TrendingUp",
                  delay: "delay-300",
                },
              ].map((adv, idx) => (
                <div
                  key={idx}
                  className={`reveal reveal-up ${adv.delay} bg-[#0a192f]/80 backdrop-blur-xl p-10 rounded-[2.5rem] shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_30px_rgba(0,243,255,0.15)] transition-all duration-500 border border-[#00f3ff]/20 hover:border-[#00f3ff]/70 hover:-translate-y-2 group cursor-pointer`}
                >
                  <div className="w-16 h-16 bg-[#00f3ff]/10 rounded-2xl flex items-center justify-center text-[#00f3ff] mb-6 border border-[#00f3ff]/30 group-hover:bg-[#00f3ff] group-hover:text-[#040d21] group-hover:shadow-[0_0_15px_#00f3ff] transition-all duration-500">
                    {(() => {
                      const IconComponent = iconMap[adv.icon] || LucideIcons.Zap;
                      return <IconComponent className="w-8 h-8 drop-shadow-[0_0_5px_currentColor]" />;
                    })()}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-[#00f3ff] transition-colors">
                    {t(`ecosystemPage.advantages.items.${idx}.title`)}
                  </h3>
                  <p className="text-slate-300 font-medium leading-relaxed">
                    {t(`ecosystemPage.advantages.items.${idx}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BEGIN: Stats Section */}
        <section className="py-24 bg-[#0a192f] relative overflow-hidden border-y border-[#00f3ff]/20">
          {/* Subtle Decorative Background */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00f3ff]/5 rounded-full blur-[100px] -z-10"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#00f3ff]/10 rounded-full blur-[120px] -z-10"></div>

          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {ecosystemStats.map((stat, idx) => {
                const numValue = Number(stat.value);
                const suffix = stat.suffix || "";

                return (
                  <div
                    key={stat.id}
                    className="reveal reveal-up group relative p-8 rounded-3xl bg-[#040d21]/60 backdrop-blur-md border border-[#00f3ff]/20 hover:border-[#00f3ff]/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(0,243,255,0.15)] overflow-hidden"
                    style={{ transitionDelay: `${(idx + 1) * 100}ms` }}
                  >
                    {/* Hover Accent Line */}
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#00f3ff] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top shadow-[0_0_10px_#00f3ff]"></div>

                    <div className="text-4xl md:text-5xl font-black text-[#00f3ff] mb-3 flex items-baseline justify-center sm:justify-start gap-1 drop-shadow-[0_0_5px_currentColor]">
                      <Counter end={numValue} suffix={suffix} />
                    </div>
                    <div className="text-slate-300 text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed text-center sm:text-left">
                      {t(`home.ecosystem.items.${stat.id}`)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Premium CTA Section */}
        <section className="py-32 px-6 bg-[#040d21]">
          <div className="max-w-6xl mx-auto bg-[#0a192f] rounded-[3.5rem] overflow-hidden relative shadow-[0_0_40px_rgba(0,0,0,0.8)] border border-[#00f3ff]/20 group">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80')] bg-cover opacity-10 group-hover:scale-110 transition-transform duration-1000"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#040d21] via-[#040d21]/80 to-transparent"></div>

            <div className="relative z-10 p-12 lg:p-24 flex flex-col lg:flex-row items-center justify-between gap-16">
              <div className="text-center lg:text-left max-w-2xl reveal reveal-up">
                <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
                  {t("ecosystemPage.cta.title")} <br />{" "}
                  <span className="text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                    {t("ecosystemPage.cta.titleAccent")}
                  </span>
                </h2>
                <p className="text-slate-300 text-xl font-light mb-10 leading-relaxed">
                  {t("ecosystemPage.cta.subtitle")}
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start">
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="bg-[#00f3ff] text-[#040d21] px-10 py-5 rounded-2xl font-black text-lg hover:shadow-[0_0_30px_rgba(0,243,255,0.6)] hover:bg-[#00f3ff]/90 transition-all hover:-translate-y-1 cursor-pointer"
                  >
                    {t("ecosystemPage.cta.joinNow")}
                  </button>
                  <button className="bg-[#040d21]/60 text-[#00f3ff] backdrop-blur-md px-10 py-5 rounded-2xl font-bold text-lg border border-[#00f3ff]/50 hover:bg-[#00f3ff]/20 transition-all cursor-pointer">
                    {t("ecosystemPage.cta.learnMore")}
                  </button>
                </div>
              </div>
              <div className="hidden lg:block w-5/12 aspect-square rounded-[2.5rem] overflow-hidden border border-[#00f3ff]/20 reveal reveal-scale delay-200 relative">
                <NextImage
                  alt="Hợp tác phát triển cùng AHV Holding - Xây dựng tương lai số vững bền"
                  className="w-full h-full object-cover transition-all duration-700 hover:scale-110"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuC6KnF84VoGjn464WVMQW0x9DSOWJztvvBszqtzeD6FWnjKp4byvKc9TBifsUtDuVSOb-VCbYNkvaRddQA_BkoWeRiTV2wV1MXzOrqkdIQKN4OKou1_LTY9RVZeQ1NS7iMjuVoCc34lV1O_AVkS5dodAJBrPwPwiZ_Sg8xGy8A_XvqywUvS2JdqBgCWQetoNwkvW75V06M09wxmY18_wjdLocIODl_bjM_OmTzpD-8KUfQNXVpzE6rfWsCkAJwNMA_hIIWJoAz4Sgc"
                  fill
                  sizes="(max-width: 1024px) 100vw, 40vw"
                />
              </div>
            </div>
          </div>
        </section>

        <MapSection />
      </main>
      <Footer />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
}
